/**
 * System Health Dashboard Types
 *
 * Type definitions for system health monitoring UI.
 */

// ============================================================================
// Worker Health Types
// ============================================================================

/** Worker health status */
export type WorkerStatus = "healthy" | "degraded" | "down";

/** Worker health info */
export interface WorkerHealth {
  readonly name: string;
  readonly status: WorkerStatus;
  readonly latencyMs: number;
  readonly errorRate: number;
  readonly requestsPerMinute: number;
  readonly lastChecked: string;
}

// ============================================================================
// D1 Database Metrics
// ============================================================================

/** D1 database metrics */
export interface D1Metrics {
  readonly queryCount: number;
  readonly avgQueryTimeMs: number;
  readonly rowsRead: number;
  readonly rowsWritten: number;
  readonly databaseSizeMb: number;
  readonly timeSeries: readonly MetricDataPoint[];
}

// ============================================================================
// KV Namespace Metrics
// ============================================================================

/** KV namespace metrics */
export interface KVMetrics {
  readonly readOperations: number;
  readonly writeOperations: number;
  readonly cacheHitRate: number;
  readonly storageUsedMb: number;
  readonly timeSeries: readonly MetricDataPoint[];
}

// ============================================================================
// R2 Bucket Metrics
// ============================================================================

/** R2 bucket metrics */
export interface R2Metrics {
  readonly objectsCount: number;
  readonly storageUsedMb: number;
  readonly getOperations: number;
  readonly putOperations: number;
  readonly deleteOperations: number;
  readonly timeSeries: readonly MetricDataPoint[];
}

// ============================================================================
// Worker Performance Metrics
// ============================================================================

/** Worker performance metrics */
export interface WorkerPerformance {
  readonly cpuTimeMs: number;
  readonly wallTimeMs: number;
  readonly requestsTotal: number;
  readonly errorsTotal: number;
  readonly timeSeries: readonly MetricDataPoint[];
}

// ============================================================================
// Common Types
// ============================================================================

/** Time-series data point */
export interface MetricDataPoint {
  readonly timestamp: string;
  readonly value: number;
}

/** Time range for metrics */
export type TimeRange = "1h" | "6h" | "24h" | "7d" | "30d";

/** System health response from API */
export interface SystemHealthResponse {
  readonly workers: readonly WorkerHealth[];
  readonly d1: D1Metrics;
  readonly kv: KVMetrics;
  readonly r2: R2Metrics;
  readonly performance: WorkerPerformance;
  readonly generatedAt: string;
}

// ============================================================================
// Constants
// ============================================================================

/** Time range labels */
export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  "1h": "Last Hour",
  "6h": "Last 6 Hours",
  "24h": "Last 24 Hours",
  "7d": "Last 7 Days",
  "30d": "Last 30 Days",
};

/** Worker status labels and colors */
export const WORKER_STATUS_INFO: Record<WorkerStatus, { label: string; color: string }> = {
  healthy: { label: "Healthy", color: "success" },
  degraded: { label: "Degraded", color: "warning" },
  down: { label: "Down", color: "error" },
};

/** Auto-refresh interval in milliseconds */
export const AUTO_REFRESH_INTERVAL = 30000;

/** Thresholds for metric highlighting */
export const THRESHOLDS = {
  latency: {
    warning: 200, // ms
    critical: 500, // ms
  },
  errorRate: {
    warning: 0.01, // 1%
    critical: 0.05, // 5%
  },
  cacheHitRate: {
    warning: 0.8, // 80%
    critical: 0.6, // 60%
  },
  cpuTime: {
    warning: 30, // ms
    critical: 50, // ms
  },
};

/** Format bytes to human-readable */
export function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/** Format number with K/M suffix */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toFixed(0);
}

/** Format percentage */
export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** Format milliseconds */
export function formatMs(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms.toFixed(0)}ms`;
}

/** Get threshold level for a metric */
export function getThresholdLevel(
  value: number,
  thresholds: { warning: number; critical: number },
  inverted = false
): "normal" | "warning" | "critical" {
  if (inverted) {
    // For metrics where lower is worse (like cache hit rate)
    if (value <= thresholds.critical) return "critical";
    if (value <= thresholds.warning) return "warning";
    return "normal";
  }
  // For metrics where higher is worse (like latency, error rate)
  if (value >= thresholds.critical) return "critical";
  if (value >= thresholds.warning) return "warning";
  return "normal";
}
